import { mapIOErrorToLocalizedObject } from '@eversports/core-io/helpers'
import { isIOError } from '@eversports/io-error'
import ClientLogger from '@eversports/react-components/utilities/client-logger'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import useCart from '../../../data/useCart'
import useCheckoutContext from '../../../data/useCheckoutContext'
import useMutationWithErrorHandling from '../../../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../../../lib/get-phoenix-url-params'
import { RequiredInformationInitialValues } from '../../../typings/AdditionalInformation'
import SkeletonRequiredInformation from '../Skeletons/SkeletonRequiredInformation'

import {
  EventType,
  RequiredInformationCartDocument,
  RequiredInformationCartQuery,
  RequiredInformationSetRequiredInformationOnCartDocument,
  RequiredInformationSetRequiredInformationOnCartMutation,
  RequiredInformationSetRequiredInformationOnCartMutationVariables,
} from '../../../graphql'
import getInitialValues from './get-initial-values'
import getValidation from './get-validation'
import RequiredInformationComponent from './RequiredInformationComponent'

const RequiredInformation: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation()
  const context = useCheckoutContext()
  const { cartId } = getPhoenixUrlParams(location.search)

  const [initialValues, setInitialValues] = useState<RequiredInformationInitialValues>()

  const { cartData, isCartLoading } = useCart<RequiredInformationCartQuery>(RequiredInformationCartDocument)

  const [
    setRequiredInformationOnCart,
    { data: setRequiredInformationOnCartData, loading: setRequiredInformationOnCartLoading },
  ] = useMutationWithErrorHandling<
    RequiredInformationSetRequiredInformationOnCartMutation,
    RequiredInformationSetRequiredInformationOnCartMutationVariables
  >(RequiredInformationSetRequiredInformationOnCartDocument, {})

  useEffect(() => {
    if (!cartData?.cart.requiredInformationInput) return

    const requiredInformationInitialValues = getInitialValues(
      cartData.cart.requiredInformationInput,
      cartData.cart.requiredInformation,
    )

    setInitialValues(requiredInformationInitialValues)
  }, [cartData?.cart.requiredInformationInput, cartData?.cart.requiredInformation])

  useEffect(() => {
    if (setRequiredInformationOnCartLoading || !setRequiredInformationOnCartData) {
      context.setIsRequiredInformationValid(false)
    } else if (
      setRequiredInformationOnCartData &&
      setRequiredInformationOnCartData.setRequiredInformationOnCart.__typename === 'Cart'
    ) {
      // Set the required information validity to true after we get back the result from the mutation
      context.setIsRequiredInformationValid(true)
    }
  }, [setRequiredInformationOnCartLoading, setRequiredInformationOnCartData])

  // Render nothing if there's no additional information to fill
  if (cartData && cartData.cart.requiredInformation.length === 0) {
    context.setIsRequiredInformationValid(true)
    return null
  }

  if (!cartData || isCartLoading || !initialValues) {
    return <SkeletonRequiredInformation />
  }

  const { venue, requiredInformation: requiredInformationTypes, items } = cartData.cart
  const cartItem = items[0]
  const isPrivateSession =
    cartItem.__typename === 'BookableItemCartItem' &&
    cartItem.bookableItem.__typename === 'EventBookableItem' &&
    cartItem.bookableItem.eventType === EventType.PRIVATE
  const validate = getValidation({
    additionalInformation: cartData.cart.requiredInformation,
    countryCode: venue.location.country,
    totalAmount: cartData.cart.priceTable?.total ?? undefined,
    customerCountryCode:
      cartData.cart.requiredInformationInput?.address?.country ||
      cartData.me?.location?.country ||
      cartData.cart.customer?.location?.country ||
      undefined,
  })

  const handleValidate = (values: RequiredInformationInitialValues) => {
    try {
      validate(values)
    } catch (e: any) {
      context.setIsRequiredInformationValid(false)
      if (isIOError(e)) {
        return mapIOErrorToLocalizedObject(e)
      }
      ClientLogger.error('Required information validation failed', e)
    }

    let address
    const { birthday, phoneNumber, fiscalCode } = values

    if (values.street && values.zip && values.city && values.country && values.detail) {
      address = {
        street: values.street,
        zip: values.zip,
        city: values.city,
        country: values.country,
        detail: values.detail,
      }
    }

    const requiredInformationInput = { address, birthday, phoneNumber, fiscalCode }

    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    setRequiredInformationOnCart({
      variables: { cartId: cartId as string, requiredInformation: requiredInformationInput },
    })
    context.setIsRequiredInformationValid(true)
    return {}
  }

  return (
    <RequiredInformationComponent
      venueName={venue.name}
      isPrivateSession={isPrivateSession}
      initialValues={initialValues}
      requiredInformationTypes={requiredInformationTypes}
      handleValidate={handleValidate}
    />
  )
}

export default RequiredInformation
